//src/pages/inventory/Product/SupplierDetailsTable.jsx
import React, { useEffect, useMemo, useState } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  styled,
  TextField,
  Stack,
  InputAdornment,
  Tooltip,
  Typography,
} from "@mui/material";
import { getAllSuppliers } from "../../../services/supplierService";
import { getInvoiceList } from "../../../services/invoiceService";
import { getMedicineInventoryList } from "../../../services/medicineInventoryService";
import { getAllInventory } from "../../../services/inventoryService";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import Refresh from "@mui/icons-material/Refresh";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import dayjs from "dayjs";
import EditSupplierModal from "./EditSupplierModal";
import AddSupplierModal from "./AddSupplierModal";
import {
  TABLE_ACTIONS_ROW_SX,
  TABLE_ADD_BUTTON_SX,
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
} from "../../../utils/pageLayoutStyles";
import {
  buildSupplierDetailsExportSheets,
  buildSupplierInvoiceHierarchy,
  flattenSupplierInvoiceItems,
  formatSupplierMoney,
  supplierMatchesFilters,
} from "../../../utils/supplierInvoiceDetailsUtils";
import { downloadSupplierDetailsTableExcel } from "../../../utils/patientTableExcelExport";
import { getOrganizationName } from "../../../utils/organizationUtils";
import { useAuth } from "../../../context/AuthContext";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

const NestedTableCell = styled(TableCell)({
  fontSize: "0.8rem",
  paddingTop: 6,
  paddingBottom: 6,
});

const toggleSetValue = (set, key) => {
  const next = new Set(set);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
};

const SupplierDetailsTable = ({ inventoryScope = "medicine" }) => {
  const { user } = useAuth();
  const isPathologyScope = inventoryScope === "pathology";
  const itemLabel = isPathologyScope ? "Path Item" : "Medicine";
  const itemsLabel = isPathologyScope ? "Path Items" : "Medicines";
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fetching, setFetching] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterContact, setFilterContact] = useState("");
  const [filterGstNumber, setFilterGstNumber] = useState("");
  const [filterId, setFilterId] = useState("");
  const [filterAddress, setFilterAddress] = useState("");
  const [filterInvoiceNumber, setFilterInvoiceNumber] = useState("");
  const [filterItemName, setFilterItemName] = useState("");
  const [filterBatchNumber, setFilterBatchNumber] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [expandedSuppliers, setExpandedSuppliers] = useState(new Set());
  const [expandedInvoices, setExpandedInvoices] = useState(new Set());

  useEffect(() => {
    fetchSuppliers();
    // Reset expand state when switching medicine/pathology scope.
    setExpandedSuppliers(new Set());
    setExpandedInvoices(new Set());
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryScope]);

  const fetchSuppliers = async () => {
    setFetching(true);
    try {
      const inventoryPromise = isPathologyScope
        ? getAllInventory().catch(() => [])
        : getMedicineInventoryList().catch(() => []);

      const [supplierData, invoiceData, scopedInventory] = await Promise.all([
        getAllSuppliers(),
        getInvoiceList({ fullRange: true }).catch(() => []),
        inventoryPromise,
      ]);

      const hierarchy = buildSupplierInvoiceHierarchy({
        suppliers: Array.isArray(supplierData) ? supplierData : [],
        invoices: Array.isArray(invoiceData) ? invoiceData : [],
        medicineInventory: isPathologyScope
          ? []
          : Array.isArray(scopedInventory)
            ? scopedInventory
            : [],
        pathInventory: isPathologyScope
          ? Array.isArray(scopedInventory)
            ? scopedInventory
            : []
          : [],
        inventoryScope,
      });
      setSuppliers(hierarchy);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      setSuppliers([]);
    }
    setFetching(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const filteredSuppliers = useMemo(
    () =>
      suppliers.filter((supplier) =>
        supplierMatchesFilters(supplier, {
          searchQuery,
          filterId,
          filterName,
          filterAddress,
          filterContact,
          filterGstNumber,
          filterInvoiceNumber,
          filterItemName,
          filterBatchNumber,
          filterDateFrom,
          filterDateTo,
        })
      ),
    [
      suppliers,
      searchQuery,
      filterId,
      filterName,
      filterAddress,
      filterContact,
      filterGstNumber,
      filterInvoiceNumber,
      filterItemName,
      filterBatchNumber,
      filterDateFrom,
      filterDateTo,
    ]
  );

  const clearFilters = () => {
    setSearchQuery("");
    setFilterId("");
    setFilterName("");
    setFilterAddress("");
    setFilterContact("");
    setFilterGstNumber("");
    setFilterInvoiceNumber("");
    setFilterItemName("");
    setFilterBatchNumber("");
    setFilterDateFrom("");
    setFilterDateTo("");
  };

  const handleEdit = (supplier) => {
    setSelectedSupplier(supplier);
    setEditModalOpen(true);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
    setSelectedSupplier(null);
  };

  const handleAddClose = () => {
    setAddModalOpen(false);
  };

  const handleExport = async () => {
    const rows = filteredSuppliers || [];
    const { sheets } = buildSupplierDetailsExportSheets(rows, {
      inventoryScope,
    });
    await downloadSupplierDetailsTableExcel({
      filename: `supplier_details_${dayjs().format("YYYY-MM-DD")}`,
      organizationName: getOrganizationName(user),
      sheets,
    });
  };

  const toggleSupplier = (supplierId) => {
    setExpandedSuppliers((prev) => toggleSetValue(prev, String(supplierId)));
  };

  const toggleInvoice = (invoiceKey) => {
    setExpandedInvoices((prev) => toggleSetValue(prev, invoiceKey));
  };

  const colSpan = 8;

  return (
    <Box>
      <Box sx={{ ...TABLE_ACTIONS_ROW_SX, px: 1.5, pt: 1, mb: 0 }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={TABLE_ADD_BUTTON_SX}
          onClick={() => setAddModalOpen(true)}
        >
          Add Supplier
        </Button>
      </Box>

      <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
        <Box sx={TABLE_SEARCH_ROW_SX}>
          <TextField
            placeholder={`Search supplier, invoice, ${isPathologyScope ? "path item" : "medicine"}, batch...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={TABLE_ICON_SX} fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
            <IconButton
              size="small"
              sx={TABLE_ICON_BUTTON_SX}
              onClick={() => setShowFilters((v) => !v)}
            >
              <FilterAltIcon
                fontSize="small"
                color={showFilters ? "primary" : "inherit"}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to Excel">
            <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              sx={TABLE_ICON_BUTTON_SX}
              onClick={async () => await fetchSuppliers()}
              disabled={fetching}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {showFilters && (
          <Stack
            spacing={1.5}
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ mt: 1, flexWrap: "wrap", rowGap: 1.5 }}
          >
            <TextField
              size="small"
              label="ID"
              type="number"
              value={filterId}
              onChange={(e) => setFilterId(e.target.value)}
              sx={{ maxWidth: 120 }}
            />
            <TextField
              size="small"
              label="Name"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
            />
            <TextField
              size="small"
              label="Address"
              value={filterAddress}
              onChange={(e) => setFilterAddress(e.target.value)}
              sx={{ minWidth: 160 }}
            />
            <TextField
              size="small"
              label="Contact"
              value={filterContact}
              onChange={(e) => setFilterContact(e.target.value)}
            />
            <TextField
              size="small"
              label="GST Number"
              value={filterGstNumber}
              onChange={(e) => setFilterGstNumber(e.target.value)}
            />
            <TextField
              size="small"
              label="Invoice No"
              value={filterInvoiceNumber}
              onChange={(e) => setFilterInvoiceNumber(e.target.value)}
            />
            <TextField
              size="small"
              label={itemLabel}
              value={filterItemName}
              onChange={(e) => setFilterItemName(e.target.value)}
            />
            <TextField
              size="small"
              label="Batch No"
              value={filterBatchNumber}
              onChange={(e) => setFilterBatchNumber(e.target.value)}
            />
            <TextField
              size="small"
              label="Invoice From"
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="Invoice To"
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Tooltip title="Clear filters">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
                <ClearAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: 48 }} />
              <StyledTableCell>ID</StyledTableCell>
              <StyledTableCell>Name</StyledTableCell>
              <StyledTableCell>Address</StyledTableCell>
              <StyledTableCell>Contact</StyledTableCell>
              <StyledTableCell>GST Number</StyledTableCell>
              <StyledTableCell>Invoices</StyledTableCell>
              <StyledTableCell>Action</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fetching ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredSuppliers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center">
                  <Alert severity="info">No suppliers found</Alert>
                </TableCell>
              </TableRow>
            ) : (
              filteredSuppliers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((supplier) => {
                  const supplierKey = String(supplier.id);
                  const isSupplierExpanded = expandedSuppliers.has(supplierKey);
                  const invoices = Array.isArray(supplier.invoices)
                    ? supplier.invoices
                    : [];

                  return (
                    <React.Fragment key={supplier.id}>
                      <TableRow hover>
                        <TableCell sx={{ width: 48 }}>
                          <IconButton
                            size="small"
                            onClick={() => toggleSupplier(supplier.id)}
                            disabled={!invoices.length}
                          >
                            {isSupplierExpanded ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>{supplier.id}</TableCell>
                        <TableCell>
                          {supplier.displayName ||
                            supplier.name ||
                            supplier.supplierName ||
                            "-"}
                        </TableCell>
                        <TableCell>{supplier.address || "-"}</TableCell>
                        <TableCell>{supplier.contact || "-"}</TableCell>
                        <TableCell>{supplier.gstNumber || "-"}</TableCell>
                        <TableCell>{supplier.invoiceCount || 0}</TableCell>
                        <TableCell>
                          <Tooltip title="Edit Supplier">
                            <IconButton
                              onClick={() => handleEdit(supplier)}
                              color="primary"
                              size="small"
                              sx={TABLE_ICON_BUTTON_SX}
                            >
                              <ModeEditOutlineTwoToneIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>

                      {isSupplierExpanded && (
                        <TableRow>
                          <TableCell
                            colSpan={colSpan}
                            sx={{ bgcolor: "grey.50", py: 1.5, px: 2 }}
                          >
                            {!invoices.length ? (
                              <Alert severity="info">
                                No stock invoices found for this supplier
                              </Alert>
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <NestedTableCell sx={{ width: 40 }} />
                                    <NestedTableCell>
                                      <strong>Invoice No</strong>
                                    </NestedTableCell>
                                    <NestedTableCell>
                                      <strong>Date</strong>
                                    </NestedTableCell>
                                    <NestedTableCell>
                                      <strong>Type</strong>
                                    </NestedTableCell>
                                    <NestedTableCell>
                                      <strong>System Amount</strong>
                                    </NestedTableCell>
                                    <NestedTableCell>
                                      <strong>Paid Amount</strong>
                                    </NestedTableCell>
                                    <NestedTableCell>
                                      <strong>{itemsLabel}</strong>
                                    </NestedTableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {invoices.map((invoice) => {
                                    const invoiceKey = `${supplierKey}-inv-${
                                      invoice.invoiceNumber ||
                                      invoice.id ||
                                      "unknown"
                                    }`;
                                    const isInvoiceExpanded =
                                      expandedInvoices.has(invoiceKey);
                                    const items = Array.isArray(invoice.items)
                                      ? invoice.items
                                      : [];

                                    return (
                                      <React.Fragment key={invoiceKey}>
                                        <TableRow hover>
                                          <NestedTableCell>
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                toggleInvoice(invoiceKey)
                                              }
                                              disabled={!items.length}
                                            >
                                              {isInvoiceExpanded ? (
                                                <ExpandLessIcon fontSize="small" />
                                              ) : (
                                                <ExpandMoreIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </NestedTableCell>
                                          <NestedTableCell>
                                            {invoice.invoiceNumber || "-"}
                                          </NestedTableCell>
                                          <NestedTableCell>
                                            {invoice.date || "-"}
                                          </NestedTableCell>
                                          <NestedTableCell>
                                            {invoice.invoiceType || "-"}
                                          </NestedTableCell>
                                          <NestedTableCell>
                                            {formatSupplierMoney(
                                              invoice.systemAmount
                                            )}
                                          </NestedTableCell>
                                          <NestedTableCell>
                                            {formatSupplierMoney(
                                              invoice.totalAmount
                                            )}
                                          </NestedTableCell>
                                          <NestedTableCell>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 0.5,
                                              }}
                                            >
                                              {items.length ? (
                                                items.map((item) => (
                                                  <Chip
                                                    key={`${invoiceKey}-${item.key}`}
                                                    size="small"
                                                    label={item.name}
                                                    variant="outlined"
                                                    color={
                                                      item.sourceType ===
                                                      "pathology"
                                                        ? "secondary"
                                                        : "primary"
                                                    }
                                                  />
                                                ))
                                              ) : (
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  No linked items
                                                </Typography>
                                              )}
                                            </Box>
                                          </NestedTableCell>
                                        </TableRow>

                                        {isInvoiceExpanded && (
                                          <TableRow>
                                            <TableCell
                                              colSpan={7}
                                              sx={{
                                                bgcolor: "background.paper",
                                                py: 1,
                                                pl: 6,
                                              }}
                                            >
                                              <Typography
                                                variant="subtitle2"
                                                sx={{ mb: 1 }}
                                              >
                                                {itemsLabel}
                                              </Typography>
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow>
                                                    <NestedTableCell>
                                                      <strong>Name</strong>
                                                    </NestedTableCell>
                                                    <NestedTableCell>
                                                      <strong>Type</strong>
                                                    </NestedTableCell>
                                                    {!isPathologyScope && (
                                                      <NestedTableCell>
                                                        <strong>
                                                          Manufacturer
                                                        </strong>
                                                      </NestedTableCell>
                                                    )}
                                                    <NestedTableCell>
                                                      <strong>Batch</strong>
                                                    </NestedTableCell>
                                                    <NestedTableCell>
                                                      <strong>Expiry</strong>
                                                    </NestedTableCell>
                                                    {isPathologyScope && (
                                                      <NestedTableCell>
                                                        <strong>
                                                          Critical Level
                                                        </strong>
                                                      </NestedTableCell>
                                                    )}
                                                    <NestedTableCell>
                                                      <strong>Qty</strong>
                                                    </NestedTableCell>
                                                    <NestedTableCell>
                                                      <strong>
                                                        Cost Per Unit
                                                      </strong>
                                                    </NestedTableCell>
                                                    <NestedTableCell>
                                                      <strong>
                                                        System Amount
                                                      </strong>
                                                    </NestedTableCell>
                                                    <NestedTableCell>
                                                      <strong>
                                                        Supplier Amount
                                                      </strong>
                                                    </NestedTableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {flattenSupplierInvoiceItems(
                                                    items
                                                  ).map((line) => (
                                                    <TableRow
                                                      hover
                                                      key={`${invoiceKey}-${line.key}`}
                                                    >
                                                      <NestedTableCell>
                                                        {line.name}
                                                      </NestedTableCell>
                                                      <NestedTableCell>
                                                        {line.sourceType ===
                                                        "pathology"
                                                          ? "Pathology"
                                                          : line.medicineType ||
                                                            "Medicine"}
                                                      </NestedTableCell>
                                                      {!isPathologyScope && (
                                                        <NestedTableCell>
                                                          {line.manufacturer ||
                                                            "-"}
                                                        </NestedTableCell>
                                                      )}
                                                      <NestedTableCell>
                                                        {line.batchNumber ||
                                                          "-"}
                                                      </NestedTableCell>
                                                      <NestedTableCell>
                                                        {line.expiryDate ||
                                                          "-"}
                                                      </NestedTableCell>
                                                      {isPathologyScope && (
                                                        <NestedTableCell>
                                                          {line.criticalLevel ??
                                                            0}
                                                        </NestedTableCell>
                                                      )}
                                                      <NestedTableCell>
                                                        {line.quantity ?? 0}
                                                      </NestedTableCell>
                                                      <NestedTableCell>
                                                        {formatSupplierMoney(
                                                          line.costPerUnit
                                                        )}
                                                      </NestedTableCell>
                                                      <NestedTableCell>
                                                        {formatSupplierMoney(
                                                          line.systemAmount
                                                        )}
                                                      </NestedTableCell>
                                                      <NestedTableCell>
                                                        {formatSupplierMoney(
                                                          line.vendorAmount
                                                        )}
                                                      </NestedTableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </Table>
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
            )}
          </TableBody>
        </Table>
        <AddSupplierModal
          open={addModalOpen}
          onClose={handleAddClose}
          onCreated={fetchSuppliers}
        />
        <EditSupplierModal
          open={editModalOpen}
          onClose={handleEditClose}
          supplierData={selectedSupplier}
          onUpdated={fetchSuppliers}
        />
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredSuppliers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default SupplierDetailsTable;
